import { RefObject, useCallback, useEffect, useRef } from 'react';

const TAB_KEY = 'Tab';

const FOCUSABLE_SELECTORS = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  'object',
  'embed',
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]'
].join(',');

const getFocusableElements = (container: HTMLElement): HTMLElement[] =>
  Array.from(
    container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS)
  ).filter(
    (element) =>
      !element.hasAttribute('disabled') &&
      element.tabIndex !== -1 &&
      !element.getAttribute('aria-hidden')
  );

interface UseDialogFocusArgs {
  isOpen: boolean;
  containerRef: RefObject<HTMLElement | null>;
  returnFocusOnClose?: boolean;
}

/**
 * Dialog focus management: on open, move focus into the container (first
 * focusable, else the container itself); trap Tab within it; and restore focus
 * to the previously-focused element on close.
 */
export const useDialogFocus = ({
  isOpen,
  containerRef,
  returnFocusOnClose = true
}: UseDialogFocusArgs): void => {
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (isOpen) {
      previouslyFocusedRef.current =
        (document.activeElement as HTMLElement) || null;
      const container = containerRef.current;
      const target =
        (container && getFocusableElements(container)[0]) || container;
      // Defer so the dialog is mounted before focusing.
      const timer = window.setTimeout(() => target?.focus(), 0);
      return () => window.clearTimeout(timer);
    }
    if (returnFocusOnClose && previouslyFocusedRef.current) {
      previouslyFocusedRef.current.focus?.();
      previouslyFocusedRef.current = null;
    }
  }, [isOpen, containerRef, returnFocusOnClose]);

  const handleTrapKeyDown = useCallback(
    (event: globalThis.KeyboardEvent) => {
      if (!isOpen || event.key !== TAB_KEY) {
        return;
      }
      const container = containerRef.current;
      if (!container) {
        return;
      }
      const focusable = getFocusableElements(container);
      if (focusable.length === 0) {
        event.preventDefault();
        container.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement as HTMLElement;
      if (event.shiftKey && (active === first || !container.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (
        !event.shiftKey &&
        (active === last || !container.contains(active))
      ) {
        event.preventDefault();
        first.focus();
      }
    },
    [isOpen, containerRef]
  );

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    document.addEventListener('keydown', handleTrapKeyDown, true);
    return () => document.removeEventListener('keydown', handleTrapKeyDown, true);
  }, [isOpen, handleTrapKeyDown]);
};
